/**
 * Phase-9 — Reusable Avatar with layered badges.
 *
 *   <Avatar user={user} size={48} />
 *
 *   • Bottom-right corner → country flag (existing behaviour)
 *   • Top-right corner    → favorite_club crest (new)
 *   • If `user.favorite_club` is null → no top badge, flag stays.
 *
 * Pure CSS positioning. Aspect-ratio preserved via square root container.
 */
import React from "react";
import { countryFlag } from "../lib/derive";

export const AVATAR_SIZE = { sm: 28, md: 40, lg: 72, xl: 128 };

export function Avatar({ user, size = 48, className = "", showBadges = true }) {
  if (!user) return null;
  // Phase-9.1: accept named size variants (sm/md/lg/xl) in addition to raw px.
  const sizePx = typeof size === "string" ? (AVATAR_SIZE[size] || 48) : size;
  const flag = countryFlag(user.country);
  const fav = user.favorite_club || null;
  const initials = (user.username || user.first_name || "?")
    .toString()
    .trim()
    .slice(0, 1)
    .toUpperCase();
  // Badge sizing: top crest = 38% of avatar, flag = 38%.
  const badge = Math.max(14, Math.round(sizePx * 0.38));
  const flagFont = Math.max(10, Math.round(sizePx * 0.32));
  const ariaLabel = [
    `User ${user.username || "anonymous"}`,
    fav?.name ? `favorite club ${fav.name}` : null,
    user.country ? `country ${user.country}` : null,
  ].filter(Boolean).join(", ");

  return (
    <div
      className={`relative inline-block ${className}`}
      style={{ width: sizePx, height: sizePx }}
      data-testid="avatar"
      aria-label={ariaLabel}
    >
      <div
        className="w-full h-full rounded-full overflow-hidden bg-gradient-to-br from-zinc-700 to-zinc-900 ring-1 ring-white/10 flex items-center justify-center text-zinc-300 font-black"
        style={{ fontSize: Math.round(sizePx * 0.42) }}
      >
        {user.avatar_url ? (
          <img
            src={user.avatar_url}
            alt=""
            referrerPolicy="no-referrer"
            className="w-full h-full object-cover"
          />
        ) : (
          <span>{initials}</span>
        )}
      </div>

      {showBadges && fav?.logo_url && (
        <span
          data-testid="avatar-favorite-club"
          className="absolute -top-0.5 -right-0.5 rounded-full bg-zinc-950 ring-2 ring-zinc-900 flex items-center justify-center overflow-hidden shadow-md"
          style={{ width: badge, height: badge }}
          title={fav.name || "favorite club"}
        >
          <img
            src={fav.logo_url}
            alt=""
            referrerPolicy="no-referrer"
            className="w-full h-full object-contain p-[2px]"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        </span>
      )}

      {showBadges && flag && (
        <span
          data-testid="avatar-country-flag"
          className="absolute -bottom-0.5 -right-0.5 rounded-full bg-zinc-950 ring-2 ring-zinc-900 flex items-center justify-center leading-none"
          style={{ width: badge, height: badge, fontSize: flagFont }}
          title={user.country || ""}
        >
          {flag}
        </span>
      )}
    </div>
  );
}

export default Avatar;
